import React from 'react';
import { Bell, BellOff, Clock, Star, X, Settings } from 'lucide-react';
import { useNotifications, subscribeUserToPush } from '../hooks/useNotifications';

interface NotificationSettingsProps {
  isVisible: boolean;
  onClose: () => void;
  scheduleData: any[];
}

export default function NotificationSettings({ isVisible, onClose, scheduleData }: NotificationSettingsProps) {
  const {
    settings,
    permission,
    requestPermission,
    updateSettings,
    getFavoriteShowDetails,
    getBrowserInstructions,
    testNotification,
  } = useNotifications(scheduleData);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (!isVisible) return null;

  const handleEnableNotifications = async () => {
    try {
      const newSettings = { ...settings, enabled: true };
      const success = await subscribeUserToPush(newSettings);
      if (success) {
        updateSettings({ enabled: true });
        if (isIOS && isSafari) {
          alert('Local notifications enabled! You can now use the "Test Local Notification" button in the settings.');
        } else {
          alert('Push notifications enabled! You will now receive show alerts and reminders.');
        }
      }
    } catch (err: any) {
        alert(err.message || 'Failed to enable notifications. Please check your browser settings and try again.');
    }
  };

  const reminderOptions = [
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black/30 bg-white/20 rounded-[2rem] border border-white/10 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-brand-pink" />
            <h2 className="text-xl font-semibold text-white">Notifications</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {isIOS && isSafari ? (
          !settings.enabled && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-semibold">iOS Safari Detected</span>
              </div>
              <p className="text-white/80 text-sm mb-4">
                Push notifications are not supported in Safari on iOS. You can still enable local notifications that work while the app is open.
              </p>
              <button
                onClick={handleEnableNotifications}
                className="w-full py-2 bg-brand-pink rounded-lg hover:bg-brand-pink/80 transition-colors text-white font-semibold"
              >
                Enable Local Notifications
              </button>
              <div className="mt-3 p-2 bg-white/5 rounded text-xs text-white/60">
                <strong>Note:</strong> These notifications will only work while the app is open. For full push notifications, use Chrome, Firefox, or Edge on iOS.
              </div>
            </div>
          )
        ) : (
          <>
            {permission === 'denied' && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <BellOff className="w-5 h-5 text-red-400" />
                  <span className="text-red-400 font-semibold">Notifications Blocked</span>
                </div>
                <p className="text-white/80 text-sm mb-4">
                  Notifications are blocked in your browser. Please enable them in your browser settings to receive show alerts.
                </p>
                <pre className="text-white/60 text-xs whitespace-pre-wrap bg-white/5 rounded p-2">
                  {typeof getBrowserInstructions === 'function' ? getBrowserInstructions() : ''}
                </pre>
              </div>
            )}

            {!settings.enabled && permission !== 'denied' && (
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-5 h-5 text-brand-pink" />
                  <span className="text-white font-semibold">Enable Notifications</span>
                </div>
                <p className="text-white/80 text-sm mb-4">
                  Get notified when your favorite shows are about to start!
                </p>
                <button
                  onClick={handleEnableNotifications}
                  className="w-full py-2 bg-brand-pink rounded-lg hover:bg-brand-pink/80 transition-colors"
                >
                  Enable Notifications
                </button>
              </div>
            )}
          </>
        )}

        {settings.enabled && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-brand-pink" />
                <span className="text-white font-semibold">Enable Notifications</span>
              </div>
              <button
                onClick={() => updateSettings({ enabled: !settings.enabled })}
                className={`relative w-12 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-brand-pink' : 'bg-white/20'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {isIOS && isSafari && (
              <div className="!mt-4">
                <button
                  onClick={() => testNotification && testNotification()}
                  className="w-full py-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/40 transition-colors text-white font-semibold text-sm"
                >
                  Test Local Notification
                </button>
              </div>
            )}

            {settings.enabled && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <span className="text-white">Show Reminders</span>
                    </div>
                    <button
                      onClick={() => updateSettings({ showScheduleReminders: !settings.showScheduleReminders })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${settings.showScheduleReminders ? 'bg-brand-pink' : 'bg-white/20'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.showScheduleReminders ? 'translate-x-7' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  
                  {settings.showScheduleReminders && (
                    <div className="ml-8">
                      <label className="block text-white/80 text-sm mb-2">Remind me before shows start:</label>
                      <select
                        value={settings.reminderTime}
                        onChange={(e) => updateSettings({ reminderTime: parseInt(e.target.value) })}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-pink"
                      >
                        {reminderOptions.map((option) => (
                          <option key={option.value} value={option.value} className="bg-gray-800">{option.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-white">New Show Alerts</span>
                  </div>
                  <button
                    onClick={() => updateSettings({ showNewShowAlerts: !settings.showNewShowAlerts })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${settings.showNewShowAlerts ? 'bg-brand-pink' : 'bg-white/20'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.showNewShowAlerts ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-brand-pink" />
                    <span className="text-white font-semibold">Favorite Shows</span>
                  </div>
                  
                  {settings.favoriteShows.length === 0 ? (
                    <div className="text-white/60 text-sm ml-8">
                      No favorite shows yet. Star shows in the schedule to get notifications!
                    </div>
                  ) : (
                    <div className="ml-8 space-y-2">
                      {settings.favoriteShows.map((showId) => {
                        const showDetails = getFavoriteShowDetails(showId);
                        return (
                          <div key={showId} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div>
                              <div className="text-white text-sm font-medium">{showDetails.artist}</div>
                              {showDetails.showName && <div className="text-white/70 text-xs">{showDetails.showName}</div>}
                            </div>
                            <button
                              onClick={() => {
                                const newFavorites = settings.favoriteShows.filter(id => id !== showId);
                                updateSettings({ favoriteShows: newFavorites });
                              }}
                              className="text-white/60 hover:text-white transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6 p-4 bg-white/5 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-4 h-4 text-white/60" />
            <span className="text-white/80 text-sm font-semibold">How it works</span>
          </div>
          <ul className="text-white/60 text-xs space-y-1">
            <li>• Get notified before your favorite shows start</li>
            <li>• Star shows in the schedule to add them to favorites</li>
            <li>• Notifications work even when the app is closed</li>
            <li>• You can adjust reminder timing to your preference</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
